'use client';

import { Laptop } from 'lucide-react';
import { useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { PostHogEvent } from '@/lib/analytics/posthog-events';
import {
  detectDownloadPlatform,
  MAC_DOWNLOAD_URL,
  type DownloadPlatform,
} from '@/lib/marketing-app-downloads';
import type { DownloadCopy } from '@/i18n/marketing-app-downloads-copy';
import { SectionLabel } from './section-label';
import { SectionShell } from './section-shell';

export function MarketingAppDownloads({ copy }: { copy: DownloadCopy }) {
  const posthog = usePostHog();
  const [platform, setPlatform] = useState<DownloadPlatform>('other');

  useEffect(() => {
    setPlatform(
      detectDownloadPlatform(
        navigator.userAgent,
        navigator.platform,
        navigator.maxTouchPoints
      )
    );
  }, []);

  const emphasized = platform === 'mac';

  return (
    <section
      className="bg-marketing-bg w-full pb-16 md:pb-24 lg:pb-28"
      aria-labelledby="app-downloads-heading"
    >
      <SectionLabel>{copy.eyebrow}</SectionLabel>
      <SectionShell className="tablet:pt-12 flex w-full flex-col gap-8 pt-6 text-left md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <h2
            id="app-downloads-heading"
            className="text-marketing-fg text-[clamp(1.5rem,3.4vw,2.75rem)] leading-[1.1] font-semibold [letter-spacing:-0.03em]"
          >
            {copy.heading}
          </h2>
          <p className="text-marketing-fg/70 mt-4 max-w-xl text-base leading-relaxed">
            {copy.description}
          </p>
        </div>
        <a
          href={MAC_DOWNLOAD_URL}
          target="_blank"
          rel="noreferrer"
          data-platform="mac"
          data-emphasized={emphasized ? 'true' : 'false'}
          onClick={() =>
            posthog?.capture(PostHogEvent.MARKETING_APP_DOWNLOAD_CLICKED, {
              platform: 'mac',
            })
          }
          className="group bg-marketing-neutral-black hover:bg-marketing-neutral-black/90 text-marketing-panel-fg focus-visible:outline-marketing-accent-strong tablet:rounded-xl inline-flex min-h-24 w-full items-center justify-center gap-3 rounded-lg px-6 text-center text-sm leading-[1.45] transition-[background-color,transform] duration-150 outline-none focus-visible:outline-2 focus-visible:outline-offset-4 motion-safe:active:scale-[0.98] sm:w-auto sm:min-w-64"
        >
          <Laptop aria-hidden="true" className="size-5" strokeWidth={1.8} />
          <span>{copy.mac}</span>
        </a>
      </SectionShell>
    </section>
  );
}
